import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import PageLayout from "../../components/PageLayout";
import ActionLoader from "../../components/ActionLoader";
import Toast from "../../components/Toast";
import {
  GET_BUYER_LOCATION_DAY_URL,
  GET_BUYER_LOCATION_LIVE_URL,
  GET_USERS_URL,
  getData,
} from "../../utils/api";
import { canAccessBuyerLocationInsights, getLoginDetails } from "../../utils/storage";
import InsightsHeaderIcon from "../../icons/newicons/insights.svg";
import { InsightsSubNav } from "./InsightsSubNav";
import {
  formatReportRangeLabel,
  getReportRange,
  shiftReportDate,
} from "./buyerLocationSample";

import "./insights.css";
import "./buyer-location.css";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const LIVE_POLL_MS = 20000;
const STALE_MS = 15 * 60 * 1000;
const DEFAULT_MAP_CENTER = [39.8283, -98.5795];
const DEFAULT_MAP_ZOOM = 4;

function parseDate(dateStr) {
  const [y, m, d] = String(dateStr).split("-").map(Number);
  return new Date(y, m - 1, d);
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function eachDate(startStr, endStr) {
  const dates = [];
  const cur = parseDate(startStr);
  const end = parseDate(endStr);
  while (cur <= end) {
    dates.push(isoDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

function emptyDayMetrics() {
  return {
    daysWorked: 0,
    distanceKm: 0,
    drivingMins: 0,
    stopCount: 0,
    stopMins: 0,
    breakCount: 0,
    breakMins: 0,
    pingCount: 0,
    avgSpeedKmh: 0,
  };
}

function addMetrics(a, b) {
  return {
    daysWorked: a.daysWorked + b.daysWorked,
    distanceKm: a.distanceKm + b.distanceKm,
    drivingMins: a.drivingMins + b.drivingMins,
    stopCount: a.stopCount + b.stopCount,
    stopMins: a.stopMins + b.stopMins,
    breakCount: a.breakCount + b.breakCount,
    breakMins: a.breakMins + b.breakMins,
    pingCount: a.pingCount + b.pingCount,
    avgSpeedKmh: 0,
  };
}

function roundKm(n) {
  return Math.round(Number(n || 0) * 10) / 10;
}

function metricsFromDayPayload(payload) {
  const summary = payload?.summary || {};
  const trail = payload?.trail || [];
  const pingCount = summary.pointCount != null ? Number(summary.pointCount) : trail.length;
  if (!pingCount) return emptyDayMetrics();
  return {
    daysWorked: 1,
    distanceKm: Number(summary.distanceKm) || 0,
    drivingMins: Number(summary.activeMins) || 0,
    stopCount: Number(summary.stopCount) || 0,
    stopMins: Number(summary.stopMins) || 0,
    breakCount: Number(summary.breakCount) || 0,
    breakMins: Number(summary.breakMins) || 0,
    pingCount,
    avgSpeedKmh: Number(summary.avgSpeedKmh) || 0,
  };
}

function buildReportFromDayMap(period, dateStr, buyers, dayByKey) {
  const range = getReportRange(period, dateStr);
  const dates = eachDate(range.start, range.end);
  const people = buyers.length ? buyers : [];

  const buyerRows = people.map((buyer) => {
    let totals = emptyDayMetrics();
    const daily = dates.map((day) => {
      const key = `${buyer.userId}::${day}`;
      const metrics = dayByKey[key] || emptyDayMetrics();
      totals = addMetrics(totals, metrics);
      return { date: day, ...metrics };
    });
    const avgKm = totals.daysWorked > 0 ? roundKm(totals.distanceKm / totals.daysWorked) : 0;
    const speedDays = daily.filter((d) => d.daysWorked && d.avgSpeedKmh);
    const avgSpeedKmh = speedDays.length
      ? Math.round(speedDays.reduce((s, d) => s + d.avgSpeedKmh, 0) / speedDays.length)
      : 0;
    return {
      userId: buyer.userId,
      userName: `${buyer.firstName || ""} ${buyer.lastName || ""}`.trim() || buyer.email || buyer.userId,
      ...totals,
      distanceKm: roundKm(totals.distanceKm),
      avgKmPerDay: avgKm,
      avgSpeedKmh,
      daily,
    };
  });

  const withActivity = buyerRows.filter((r) => r.daysWorked > 0 || r.pingCount > 0);
  withActivity.sort((a, b) => b.distanceKm - a.distanceKm);

  const team = withActivity.reduce((acc, row) => addMetrics(acc, row), emptyDayMetrics());
  team.distanceKm = roundKm(team.distanceKm);
  team.buyersActive = withActivity.filter((r) => r.daysWorked > 0).length;
  team.avgKmPerBuyer = team.buyersActive > 0 ? roundKm(team.distanceKm / team.buyersActive) : 0;

  const buckets = dates.map((day) => {
    const ofDay = withActivity.reduce((acc, row) => {
      const m = row.daily.find((d) => d.date === day) || emptyDayMetrics();
      return addMetrics(acc, m);
    }, emptyDayMetrics());
    return {
      date: day,
      label: parseDate(day).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
      distanceKm: roundKm(ofDay.distanceKm),
      drivingMins: ofDay.drivingMins,
      stopCount: ofDay.stopCount,
      breakCount: ofDay.breakCount,
      daysWorked: ofDay.daysWorked,
    };
  });

  return {
    empty: withActivity.length === 0,
    period,
    ...range,
    label: formatReportRangeLabel(period, dateStr),
    buyers: withActivity,
    team,
    buckets,
  };
}

function formatTime(ms) {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function minsLabel(mins) {
  if (mins == null) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function mph(mps) {
  if (mps == null || !Number.isFinite(Number(mps))) return "—";
  return `${Math.round(Number(mps) * 2.237)} mph`;
}

function pingAge(ms) {
  if (!ms) return "—";
  const mins = Math.max(0, Math.round((Date.now() - Number(ms)) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
}

function activityOf(p) {
  if (!p) return "unknown";
  if (p.isBreak || p.activity === "break") return "break";
  if (p.activity === "stationary") return "stop";
  if (p.activity === "moving") return "moving";
  return p.activity || "unknown";
}

function activityColor(kind) {
  if (kind === "break") return "#c82534";
  if (kind === "stop") return "#b58100";
  if (kind === "moving") return "#2e7d32";
  return "#6c757d";
}

function buyerDisplayName(buyer) {
  if (!buyer) return "Buyer";
  const name = `${buyer.firstName || ""} ${buyer.lastName || ""}`.trim();
  return name || buyer.email || buyer.userId || "Buyer";
}

function liveKpis(list) {
  let moving = 0;
  let stop = 0;
  let brk = 0;
  let lowBatt = 0;
  let stale = 0;
  let accuracySum = 0;
  let accuracyN = 0;
  (list || []).forEach((p) => {
    const kind = activityOf(p);
    if (kind === "break") brk += 1;
    else if (kind === "stop") stop += 1;
    else if (kind === "moving") moving += 1;
    if (p.battery != null && Number(p.battery) <= 20) lowBatt += 1;
    if (p.recordedAt && Date.now() - Number(p.recordedAt) > STALE_MS) stale += 1;
    if (p.accuracy != null) {
      accuracySum += Number(p.accuracy);
      accuracyN += 1;
    }
  });
  return {
    total: (list || []).length,
    moving,
    stop,
    brk,
    lowBatt,
    stale,
    avgAccuracyM: accuracyN ? Math.round(accuracySum / accuracyN) : null,
  };
}

function enrichDaySummary(dayData) {
  const base = dayData?.summary || {};
  const trail = dayData?.trail || [];
  if (!trail.length) return base;
  const speeds = trail.map((p) => Number(p.speed) || 0);
  const accuracies = trail.filter((p) => p.accuracy != null).map((p) => Number(p.accuracy));
  const first = trail[0];
  const last = trail[trail.length - 1];
  return {
    ...base,
    pointCount: base.pointCount != null ? base.pointCount : trail.length,
    avgSpeedKmh:
      base.avgSpeedKmh != null
        ? base.avgSpeedKmh
        : Math.round((speeds.reduce((s, n) => s + n, 0) / Math.max(1, speeds.length)) * 3.6),
    maxSpeedKmh:
      base.maxSpeedKmh != null ? base.maxSpeedKmh : Math.round(Math.max(0, ...speeds) * 3.6),
    avgAccuracyM:
      base.avgAccuracyM != null
        ? base.avgAccuracyM
        : accuracies.length
          ? Math.round(accuracies.reduce((s, n) => s + n, 0) / accuracies.length)
          : null,
    batteryStart: base.batteryStart != null ? base.batteryStart : first.battery,
    batteryEnd: base.batteryEnd != null ? base.batteryEnd : last.battery,
  };
}

const BuyerLocation = () => {
  const loginDetails = getLoginDetails();
  const canView = canAccessBuyerLocationInsights(loginDetails);

  const [mode, setMode] = useState("live");
  const [reportPeriod, setReportPeriod] = useState("week");
  const [reportBuyerId, setReportBuyerId] = useState("");
  const [report, setReport] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyerId, setSelectedBuyerId] = useState("");
  const [buyerSearch, setBuyerSearch] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [liveList, setLiveList] = useState([]);
  const [dayData, setDayData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toastParams, setToastParams] = useState({ message: "", type: "", id: 0 });

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!canView) {
      window.location.href = "/";
    }
  }, [canView]);

  const loadBuyers = useCallback(async () => {
    try {
      const data = await getData(GET_USERS_URL, {
        count: 500,
        offset: 0,
        text: "",
        companyId: loginDetails.companyId,
        isBuyer: true,
      });
      setBuyers(data.list || []);
    } catch (e) {
      console.log("ERROR loading buyers", e);
      setBuyers([]);
    }
  }, [loginDetails.companyId]);

  const fetchLive = useCallback(async () => {
    try {
      const data = await getData(GET_BUYER_LOCATION_LIVE_URL, {});
      const list = data.list || data?.data?.list || [];
      setLiveList(list);
    } catch (e) {
      console.log("ERROR live locations", e);
      setLiveList([]);
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : "Failed to load live locations",
        id: Date.now(),
      });
    }
  }, []);

  const fetchDay = useCallback(async () => {
    if (!selectedBuyerId) {
      setDayData(null);
      return;
    }
    try {
      setIsLoading(true);
      const data = await getData(GET_BUYER_LOCATION_DAY_URL, {
        userId: selectedBuyerId,
        date,
      });
      const payload = data?.trail != null ? data : data?.data || data;
      setDayData(payload || null);
    } catch (e) {
      console.log("ERROR day trail", e);
      setDayData(null);
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : "Failed to load day trail",
        id: Date.now(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedBuyerId, date]);

  const fetchReport = useCallback(async () => {
    const people = buyers.length
      ? buyers
      : (liveList || [])
          .filter((p) => p.userId)
          .map((p) => ({
            userId: p.userId,
            firstName: (p.userName || "").split(" ")[0] || "",
            lastName: (p.userName || "").split(" ").slice(1).join(" "),
            email: "",
          }));

    if (!people.length) {
      setReport({
        empty: true,
        period: reportPeriod,
        label: formatReportRangeLabel(reportPeriod, date),
        buyers: [],
        team: emptyDayMetrics(),
        buckets: [],
      });
      return;
    }

    const range = getReportRange(reportPeriod, date);
    const dates = eachDate(range.start, range.end);
    const dayByKey = {};

    try {
      setIsLoading(true);
      const jobs = [];
      people.forEach((buyer) => {
        dates.forEach((day) => {
          jobs.push({ buyer, day });
        });
      });

      const CONCURRENCY = 6;
      for (let i = 0; i < jobs.length; i += CONCURRENCY) {
        const chunk = jobs.slice(i, i + CONCURRENCY);
        await Promise.all(
          chunk.map(async ({ buyer, day }) => {
            try {
              const data = await getData(GET_BUYER_LOCATION_DAY_URL, {
                userId: buyer.userId,
                date: day,
              });
              const payload = data?.trail != null ? data : data?.data || data;
              dayByKey[`${buyer.userId}::${day}`] = metricsFromDayPayload(payload);
            } catch (e) {
              dayByKey[`${buyer.userId}::${day}`] = emptyDayMetrics();
            }
          })
        );
      }

      setReport(buildReportFromDayMap(reportPeriod, date, people, dayByKey));
    } catch (e) {
      console.log("ERROR report", e);
      setReport({
        empty: true,
        period: reportPeriod,
        label: formatReportRangeLabel(reportPeriod, date),
        buyers: [],
        team: emptyDayMetrics(),
        buckets: [],
      });
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : "Failed to load location report",
        id: Date.now(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [buyers, liveList, reportPeriod, date]);

  useEffect(() => {
    if (!canView) return;
    loadBuyers();
  }, [canView, loadBuyers]);

  useEffect(() => {
    if (!canView || mode === "report") return undefined;
    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      if (mode === "live") {
        await fetchLive();
      } else {
        await fetchDay();
      }
      if (!cancelled) setIsLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [canView, mode, fetchLive, fetchDay]);

  useEffect(() => {
    if (!canView || mode !== "live") return undefined;
    const id = setInterval(() => {
      fetchLive();
    }, LIVE_POLL_MS);
    return () => clearInterval(id);
  }, [canView, mode, fetchLive]);

  useEffect(() => {
    if (!canView || mode !== "report") return;
    fetchReport();
  }, [canView, mode, fetchReport]);

  const buyerOptions = useMemo(() => {
    const byId = new Map();
    (buyers || []).forEach((b) => {
      if (b?.userId) byId.set(b.userId, b);
    });
    (liveList || []).forEach((p) => {
      if (!p?.userId || byId.has(p.userId)) return;
      const parts = String(p.userName || "").trim().split(/\s+/);
      byId.set(p.userId, {
        userId: p.userId,
        firstName: parts[0] || p.userName || p.userId,
        lastName: parts.slice(1).join(" "),
        email: "",
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      buyerDisplayName(a).localeCompare(buyerDisplayName(b))
    );
  }, [buyers, liveList]);

  const filteredBuyers = useMemo(() => {
    const q = buyerSearch.trim().toLowerCase();
    if (!q) return buyerOptions;
    return buyerOptions.filter((b) => {
      const name = `${b.firstName || ""} ${b.lastName || ""} ${b.email || ""}`.toLowerCase();
      return name.includes(q);
    });
  }, [buyerOptions, buyerSearch]);

  const displayLive = liveList;
  const displayDay = dayData;

  const reportMaxDistance = useMemo(() => {
    if (!report?.buckets?.length) return 1;
    return Math.max(1, ...report.buckets.map((b) => b.distanceKm));
  }, [report]);

  const kpis = useMemo(() => liveKpis(displayLive), [displayLive]);
  const summary = enrichDaySummary(displayDay);
  const selectedBuyer = buyerOptions.find((b) => b.userId === selectedBuyerId);

  useEffect(() => {
    if (!canView || !mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    layerRef.current = L.layerGroup().addTo(map);
    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      layerRef.current = null;
    };
  }, [canView]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;

    if (mode === "report") return;
    layer.clearLayers();
    const bounds = [];

    if (mode === "live") {
      displayLive.forEach((p) => {
        if (p.lat == null || p.lng == null) return;
        const kind = activityOf(p);
        const color = activityColor(kind);
        const stale = p.recordedAt && Date.now() - Number(p.recordedAt) > STALE_MS;
        L.circleMarker([p.lat, p.lng], {
          radius: 9,
          color: "#fff",
          weight: 2,
          fillColor: color,
          fillOpacity: stale ? 0.45 : 0.95,
        })
          .bindPopup(
            `<strong>${p.userName || "Buyer"}</strong><br/>
            ${kind}${stale ? " · stale" : ""}<br/>
            ${mph(p.speed)} · GPS ±${p.accuracy != null ? p.accuracy : "—"}m<br/>
            batt ${p.battery != null ? `${p.battery}%` : "—"} · ${p.deviceTypeId || "—"} ${p.appVersion || ""}<br/>
            ${formatTime(p.recordedAt)} (${pingAge(p.recordedAt)})`
          )
          .addTo(layer);
        bounds.push([p.lat, p.lng]);
      });
    } else if (displayDay?.trail?.length) {
      const latlngs = displayDay.trail.map((p) => [p.lat, p.lng]);
      L.polyline(latlngs, { color: "#c82534", weight: 4, opacity: 0.85 }).addTo(layer);
      latlngs.forEach((ll) => bounds.push(ll));

      const start = displayDay.trail[0];
      const end = displayDay.trail[displayDay.trail.length - 1];
      L.circleMarker([start.lat, start.lng], {
        radius: 8,
        fillColor: "#2e7d32",
        color: "#fff",
        weight: 2,
        fillOpacity: 1,
      })
        .bindPopup(`Start · ${formatTime(start.recordedAt)}`)
        .addTo(layer);
      L.circleMarker([end.lat, end.lng], {
        radius: 8,
        fillColor: "#1565c0",
        color: "#fff",
        weight: 2,
        fillOpacity: 1,
      })
        .bindPopup(`End · ${formatTime(end.recordedAt)}`)
        .addTo(layer);

      (displayDay.events || []).forEach((ev) => {
        const color = ev.type === "break" ? "#c82534" : "#b58100";
        L.circleMarker([ev.lat, ev.lng], {
          radius: 10,
          fillColor: color,
          color: "#fff",
          weight: 2,
          fillOpacity: 0.95,
        })
          .bindPopup(
            `${ev.label}<br/>${formatTime(ev.startAt)} – ${formatTime(ev.endAt)}${
              ev.durationMins ? ` · ${minsLabel(ev.durationMins)}` : ""
            }`
          )
          .addTo(layer);
        bounds.push([ev.lat, ev.lng]);
      });
    }

    if (bounds.length === 1) {
      map.setView(bounds[0], 13);
    } else if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [40, 40] });
    } else {
      map.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);
    }
    setTimeout(() => map.invalidateSize(), 50);
  }, [mode, displayLive, displayDay]);

  if (!canView) return null;

  return (
    <PageLayout icon={InsightsHeaderIcon} heading="Insights" searchPlaceholder="" hideSearchBar>
      <InsightsSubNav />

      <div className="buyer-loc-page">
        <div className="buyer-loc-header">
          <div>
            <h2 className="buyer-loc-title">Buyer Location</h2>
            <p className="buyer-loc-subtitle">
              Track field buyers live, review day trails, and roll up distance, driving, stops, and
              breaks by day, week, or month.
            </p>
          </div>
          <div className="buyer-loc-header-actions">
            <div className="buyer-loc-mode">
              <button
                type="button"
                className={`buyer-loc-mode-btn${mode === "live" ? " is-active" : ""}`}
                onClick={() => setMode("live")}
              >
                Live map
              </button>
              <button
                type="button"
                className={`buyer-loc-mode-btn${mode === "day" ? " is-active" : ""}`}
                onClick={() => setMode("day")}
              >
                Day trail
              </button>
              <button
                type="button"
                className={`buyer-loc-mode-btn${mode === "report" ? " is-active" : ""}`}
                onClick={() => setMode("report")}
              >
                Reports
              </button>
            </div>
          </div>
        </div>

        <div className="buyer-loc-toolbar">
          {mode === "report" && (
            <>
              <div className="buyer-loc-mode">
                {["day", "week", "month"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`buyer-loc-mode-btn${reportPeriod === p ? " is-active" : ""}`}
                    onClick={() => setReportPeriod(p)}
                  >
                    {p === "day" ? "Day" : p === "week" ? "Week" : "Month"}
                  </button>
                ))}
              </div>
              <div className="buyer-loc-nav-row">
                <button
                  type="button"
                  className="buyer-loc-nav-btn"
                  onClick={() => setDate(shiftReportDate(date, reportPeriod, -1))}
                >
                  Prev
                </button>
                <div className="buyer-loc-field">
                  <label>{reportPeriod === "month" ? "Month" : reportPeriod === "week" ? "Week of" : "Date"}</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <button
                  type="button"
                  className="buyer-loc-nav-btn"
                  onClick={() => setDate(shiftReportDate(date, reportPeriod, 1))}
                >
                  Next
                </button>
              </div>
            </>
          )}
          {mode === "day" && (
            <>
              <div className="buyer-loc-field">
                <label>Buyer</label>
                <input
                  type="text"
                  placeholder="Search buyers…"
                  value={buyerSearch}
                  onChange={(e) => setBuyerSearch(e.target.value)}
                />
                <select
                  value={selectedBuyerId}
                  onChange={(e) => setSelectedBuyerId(e.target.value)}
                >
                  <option value="">Select buyer</option>
                  {filteredBuyers.map((b) => (
                    <option key={b.userId} value={b.userId}>
                      {buyerDisplayName(b)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="buyer-loc-field">
                <label>Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <button type="button" className="buyer-loc-refresh" onClick={() => fetchDay()}>
                Refresh
              </button>
            </>
          )}
          {mode === "live" && (
            <button type="button" className="buyer-loc-refresh" onClick={() => fetchLive()}>
              Refresh live
            </button>
          )}
          {mode === "report" && (
            <button type="button" className="buyer-loc-refresh" onClick={() => fetchReport()}>
              Refresh report
            </button>
          )}
          {mode !== "report" && (
            <div className="buyer-loc-legend">
              <span><i className="buyer-loc-dot buyer-loc-dot--moving" /> Moving</span>
              <span><i className="buyer-loc-dot buyer-loc-dot--stop" /> Stop</span>
              <span><i className="buyer-loc-dot buyer-loc-dot--break" /> Break</span>
            </div>
          )}
        </div>

        {mode === "live" && (
          <div className="buyer-loc-summary">
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Active buyers</div>
              <div className="buyer-loc-summary-value">{kpis.total}</div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Moving</div>
              <div className="buyer-loc-summary-value">{kpis.moving}</div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">At stop</div>
              <div className="buyer-loc-summary-value">{kpis.stop}</div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">On break</div>
              <div className="buyer-loc-summary-value">{kpis.brk}</div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Low battery</div>
              <div className="buyer-loc-summary-value">{kpis.lowBatt}</div>
              <div className="buyer-loc-summary-hint">≤ 20%</div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Stale ping</div>
              <div className="buyer-loc-summary-value">{kpis.stale}</div>
              <div className="buyer-loc-summary-hint">&gt; 15 min</div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Avg GPS accuracy</div>
              <div className="buyer-loc-summary-value">
                {kpis.avgAccuracyM != null ? `±${kpis.avgAccuracyM}m` : "—"}
              </div>
            </div>
          </div>
        )}

        {mode === "day" && summary && displayDay?.trail?.length > 0 && (
          <div className="buyer-loc-summary buyer-loc-summary--day">
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Distance</div>
              <div className="buyer-loc-summary-value">{summary.distanceKm} km</div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Driving</div>
              <div className="buyer-loc-summary-value">{minsLabel(summary.activeMins)}</div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Stops</div>
              <div className="buyer-loc-summary-value">
                {summary.stopCount} · {minsLabel(summary.stopMins)}
              </div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Breaks</div>
              <div className="buyer-loc-summary-value">
                {summary.breakCount} · {minsLabel(summary.breakMins)}
              </div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">First → last ping</div>
              <div className="buyer-loc-summary-value buyer-loc-summary-value--sm">
                {formatTime(summary.firstPingAt)} – {formatTime(summary.lastPingAt)}
              </div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Pings</div>
              <div className="buyer-loc-summary-value">{summary.pointCount || "—"}</div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Avg / max speed</div>
              <div className="buyer-loc-summary-value buyer-loc-summary-value--sm">
                {summary.avgSpeedKmh != null ? `${summary.avgSpeedKmh}` : "—"} /{" "}
                {summary.maxSpeedKmh != null ? `${summary.maxSpeedKmh}` : "—"} km/h
              </div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Avg GPS accuracy</div>
              <div className="buyer-loc-summary-value">
                {summary.avgAccuracyM != null ? `±${summary.avgAccuracyM}m` : "—"}
              </div>
            </div>
            <div className="buyer-loc-summary-card">
              <div className="buyer-loc-summary-label">Battery</div>
              <div className="buyer-loc-summary-value buyer-loc-summary-value--sm">
                {summary.batteryStart != null ? `${summary.batteryStart}%` : "—"} →{" "}
                {summary.batteryEnd != null ? `${summary.batteryEnd}%` : "—"}
              </div>
            </div>
          </div>
        )}

        {mode === "report" && (
          <div className="buyer-loc-report">
            {isLoading && <ActionLoader />}
            <div className="buyer-loc-report-range">
              {report?.label || formatReportRangeLabel(reportPeriod, date)}
            </div>

            {!isLoading && (!report || report.empty) ? (
              <div className="buyer-loc-empty">
                No BuyerLocation pings found for this period. Once buyers post location from the
                phone app, day / week / month rollups will show here.
              </div>
            ) : !isLoading && report ? (
              <>
                <div className="buyer-loc-summary buyer-loc-summary--day">
                  <div className="buyer-loc-summary-card">
                    <div className="buyer-loc-summary-label">Buyers active</div>
                    <div className="buyer-loc-summary-value">{report.team.buyersActive}</div>
                  </div>
                  <div className="buyer-loc-summary-card">
                    <div className="buyer-loc-summary-label">Total distance</div>
                    <div className="buyer-loc-summary-value">{report.team.distanceKm} km</div>
                    <div className="buyer-loc-summary-hint">
                      {report.team.avgKmPerBuyer} km / buyer
                    </div>
                  </div>
                  <div className="buyer-loc-summary-card">
                    <div className="buyer-loc-summary-label">Total driving</div>
                    <div className="buyer-loc-summary-value">{minsLabel(report.team.drivingMins)}</div>
                  </div>
                  <div className="buyer-loc-summary-card">
                    <div className="buyer-loc-summary-label">Stops</div>
                    <div className="buyer-loc-summary-value">
                      {report.team.stopCount} · {minsLabel(report.team.stopMins)}
                    </div>
                  </div>
                  <div className="buyer-loc-summary-card">
                    <div className="buyer-loc-summary-label">Breaks</div>
                    <div className="buyer-loc-summary-value">
                      {report.team.breakCount} · {minsLabel(report.team.breakMins)}
                    </div>
                  </div>
                  <div className="buyer-loc-summary-card">
                    <div className="buyer-loc-summary-label">Days worked</div>
                    <div className="buyer-loc-summary-value">{report.team.daysWorked}</div>
                    <div className="buyer-loc-summary-hint">across the team</div>
                  </div>
                  <div className="buyer-loc-summary-card">
                    <div className="buyer-loc-summary-label">Pings</div>
                    <div className="buyer-loc-summary-value">{report.team.pingCount}</div>
                  </div>
                </div>

                {reportPeriod !== "day" && (
                  <div className="buyer-loc-chart-card">
                    <div className="buyer-loc-chart-title">Team distance by day</div>
                    <div className={`buyer-loc-chart${reportPeriod === "month" ? " is-month" : ""}`}>
                      {report.buckets.map((b) => (
                        <div key={b.date} className="buyer-loc-chart-col" title={`${b.label}: ${b.distanceKm} km`}>
                          <div className="buyer-loc-chart-bar-wrap">
                            <div
                              className={`buyer-loc-chart-bar${b.distanceKm === 0 ? " is-empty" : ""}`}
                              style={{ height: `${Math.max(4, (b.distanceKm / reportMaxDistance) * 100)}%` }}
                            />
                          </div>
                          <div className="buyer-loc-chart-label">
                            {reportPeriod === "month"
                              ? b.date.slice(8)
                              : b.label.split(",")[0]}
                          </div>
                          <div className="buyer-loc-chart-val">{b.distanceKm || "–"}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="buyer-loc-table-wrap">
                  <table className="buyer-loc-table">
                    <thead>
                      <tr>
                        <th>Buyer</th>
                        {reportPeriod !== "day" && <th>Days</th>}
                        <th>Distance</th>
                        <th>Driving</th>
                        <th>Stops</th>
                        <th>Stop time</th>
                        <th>Breaks</th>
                        <th>Break time</th>
                        {reportPeriod !== "day" && <th>Avg km/day</th>}
                        <th>Pings</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {report.buyers.map((row) => {
                        const open = reportBuyerId === row.userId;
                        return (
                          <React.Fragment key={row.userId}>
                            <tr
                              className={open ? "is-open" : ""}
                              onClick={() =>
                                setReportBuyerId(open ? "" : row.userId)
                              }
                            >
                              <td className="buyer-loc-table-name">{row.userName}</td>
                              {reportPeriod !== "day" && <td>{row.daysWorked}</td>}
                              <td>{row.distanceKm} km</td>
                              <td>{minsLabel(row.drivingMins)}</td>
                              <td>{row.stopCount}</td>
                              <td>{minsLabel(row.stopMins)}</td>
                              <td>{row.breakCount}</td>
                              <td>{minsLabel(row.breakMins)}</td>
                              {reportPeriod !== "day" && <td>{row.avgKmPerDay} km</td>}
                              <td>{row.pingCount}</td>
                              <td>
                                <button
                                  type="button"
                                  className="buyer-loc-link-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedBuyerId(row.userId);
                                    setMode("day");
                                  }}
                                >
                                  Trail
                                </button>
                              </td>
                            </tr>
                            {open && reportPeriod !== "day" && (
                              <tr className="buyer-loc-table-detail">
                                <td colSpan={11}>
                                  <div className="buyer-loc-daily-grid">
                                    {row.daily.map((d) => (
                                      <div
                                        key={d.date}
                                        className={`buyer-loc-daily-cell${d.daysWorked ? "" : " is-off"}`}
                                      >
                                        <div className="buyer-loc-daily-date">
                                          {new Date(`${d.date}T12:00:00`).toLocaleDateString("en-US", {
                                            weekday: "short",
                                            month: "short",
                                            day: "numeric",
                                          })}
                                        </div>
                                        {d.daysWorked ? (
                                          <>
                                            <div>{d.distanceKm} km</div>
                                            <div>{minsLabel(d.drivingMins)} drive</div>
                                            <div>
                                              {d.stopCount} stops · {d.breakCount} breaks
                                            </div>
                                          </>
                                        ) : (
                                          <div>Off</div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </div>
        )}

        <div className="buyer-loc-layout" style={{ display: mode === "report" ? "none" : undefined }}>
          <div className="buyer-loc-map-wrap">
            {isLoading && <ActionLoader />}
            <div ref={mapRef} className="buyer-loc-map" />
            {!isLoading && mode === "live" && displayLive.length === 0 && (
              <div className="buyer-loc-map-empty">No buyers reporting location right now.</div>
            )}
            {!isLoading && mode === "day" && !selectedBuyerId && (
              <div className="buyer-loc-map-empty">Select a buyer and date to view their trail.</div>
            )}
            {!isLoading &&
              mode === "day" &&
              selectedBuyerId &&
              !displayDay?.trail?.length && (
                <div className="buyer-loc-map-empty">
                  No location data for this buyer on the selected date.
                </div>
              )}
          </div>

          <div className="buyer-loc-side">
            {mode === "live" ? (
              <>
                <h3 className="buyer-loc-side-title">Buyers on the field</h3>
                <p className="buyer-loc-side-hint">
                  Auto-refreshes every {LIVE_POLL_MS / 1000}s · {displayLive.length} active
                </p>
                <div className="buyer-loc-list">
                  {displayLive.length === 0 && (
                    <div className="buyer-loc-empty">
                      Waiting for location pings from the buyer app.
                    </div>
                  )}
                  {displayLive.map((p, idx) => {
                    const kind = activityOf(p);
                    const stale = p.recordedAt && Date.now() - Number(p.recordedAt) > STALE_MS;
                    return (
                      <div key={`${p.userId || p.userName}-${idx}`} className="buyer-loc-list-item">
                        <div className="buyer-loc-list-name">
                          {p.userName || "Buyer"}
                          {stale ? <span className="buyer-loc-stale">stale</span> : null}
                        </div>
                        <div className="buyer-loc-list-meta">
                          <span className={`buyer-loc-pill buyer-loc-pill--${kind}`}>{kind}</span>
                          <span>{pingAge(p.recordedAt)}</span>
                          <span>{formatTime(p.recordedAt)}</span>
                        </div>
                        <div className="buyer-loc-list-stats">
                          <span>{mph(p.speed)}</span>
                          <span>GPS ±{p.accuracy != null ? p.accuracy : "—"}m</span>
                          <span className={p.battery != null && p.battery <= 20 ? "is-warn" : ""}>
                            batt {p.battery != null ? `${p.battery}%` : "—"}
                          </span>
                        </div>
                        <div className="buyer-loc-list-coords">
                          {Number(p.lat).toFixed(5)}, {Number(p.lng).toFixed(5)}
                          {p.heading != null ? ` · hdg ${Math.round(p.heading)}°` : ""}
                          {p.altitude != null ? ` · alt ${Math.round(p.altitude)}m` : ""}
                        </div>
                        <div className="buyer-loc-list-coords">
                          {p.deviceTypeId || "device —"} · app {p.appVersion || "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <h3 className="buyer-loc-side-title">Day timeline</h3>
                <p className="buyer-loc-side-hint">
                  {selectedBuyer
                    ? buyerDisplayName(selectedBuyer)
                    : displayDay?.userName || "Select a buyer"}{" "}
                  · {displayDay?.date || date}
                </p>
                <div className="buyer-loc-timeline">
                  {!selectedBuyerId && (
                    <div className="buyer-loc-empty">Select a buyer to see stops and breaks.</div>
                  )}
                  {selectedBuyerId && !(displayDay?.events || []).length && (
                    <div className="buyer-loc-empty">No stops or breaks detected for this day.</div>
                  )}
                  {(displayDay?.events || []).map((ev, idx) => (
                    <div key={idx} className={`buyer-loc-event buyer-loc-event--${ev.type}`}>
                      <div className="buyer-loc-event-label">{ev.label}</div>
                      <div className="buyer-loc-event-meta">
                        {formatTime(ev.startAt)} – {formatTime(ev.endAt)}
                        {ev.durationMins != null ? ` · ${minsLabel(ev.durationMins)}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {toastParams.message && (
        <Toast type={toastParams.type} message={toastParams.message} id={toastParams.id} />
      )}
    </PageLayout>
  );
};

export default BuyerLocation;
